import { useEffect, useState, useRef } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import camera from 'static/camera.svg';
import { Container } from '~/components/layout';
import { IS_PERCH_ENERGY } from '~/utils/constants';
import { toBase64, UploadedImage } from '~/utils/files';
import { ImagePreview } from './image-preview';
import type { ImagePreviewValidationStatus } from './image-preview/image-preview.component';
import { ImageReviewDialog } from './image-review-dialog';
import { Dropzone, DropzoneCt } from './image-uploader.style';
import { PdfReviewDialog } from './pdf-review-dialog/pdf-review-dialog.component';

export interface ImageUploaderProps {
  acceptFileTypes: { [key: string]: string[] };
  copy: { documentType: string };
  deferUploadUntilSave?: boolean;
  uploadedImages: UploadedImage[];
  maxImages: number;
  pageType: 'numbered' | 'front-back';
  pending: boolean;
  validationStatuses?: Record<string, ImagePreviewValidationStatus>;
  onUploadSuccess: (file: File, base64Image: string) => void;
  onUploadFailure: (file: FileRejection) => void;
  onDelete: (id: string) => void;
  onRender?: (images: UploadedImage[]) => void;
}

type ImageFile = {
  file: File;
  base64Image: string;
};

const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

interface UploaderProps {
  getRootProps: () => any;
  getInputProps: () => any;
  isDragActive: boolean;
  handleDropzoneClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  uploadNextPageMessage: () => string;
}

const MobileUploader = ({
  getRootProps,
  getInputProps,
  isDragActive,
  handleDropzoneClick,
  fileInputRef,
  handleFileInputChange,
}: UploaderProps) => (
  <Dropzone {...getRootProps()} onClick={handleDropzoneClick}>
    <input {...getInputProps()} style={{ display: 'none' }} />

    <input
      ref={fileInputRef}
      data-testid="dropzone"
      type="file"
      accept="image/*,application/pdf"
      onChange={handleFileInputChange}
      style={{ display: 'none' }}
    />

    <Container pt={1}>
      <img src={camera} alt="Camera" />
    </Container>
    <b>{isDragActive ? 'Drop to upload' : 'Tap to upload or take photo'}</b>
  </Dropzone>
);

const DesktopUploader = ({
  getRootProps,
  getInputProps,
  isDragActive,
  uploadNextPageMessage,
}: UploaderProps) => (
  <Dropzone {...getRootProps()}>
    <input {...getInputProps()} data-testid="dropzone" />

    <Container pt={1}>
      <img src={camera} alt="Camera" />
    </Container>
    <b>{isDragActive ? 'Drop to upload' : uploadNextPageMessage()}</b>
  </Dropzone>
);

export const ImageUploader = ({
  acceptFileTypes,
  copy,
  deferUploadUntilSave = false,
  uploadedImages,
  maxImages,
  pageType,
  onDelete,
  onUploadFailure,
  onUploadSuccess,
  onRender,
  pending,
  validationStatuses,
}: ImageUploaderProps) => {
  const isMobile = isMobileDevice();
  const [reviewImageFile, setReviewImageFile] = useState<ImageFile | null>(null);
  const [reviewImageFileType, setReviewImageFileType] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploadComplete = (image: UploadedImage) =>
    !!image.uploadId || (!!deferUploadUntilSave && !!image.base64Image && !image.error);
  const totalSavedFiles = uploadedImages.filter(isUploadComplete).length;
  const acceptMoreFiles = totalSavedFiles < maxImages;

  const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
    if (accepted.length) {
      const file = accepted[0];
      const base64Image = await toBase64(file);
      setReviewImageFile({ file, base64Image });
      setReviewImageFileType(file.type);
    } else if (rejected.length) {
      const rejectedFile = rejected[0];
      onUploadFailure(rejectedFile);
    }
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const file = files[0];

      const isValidType = Object.values(acceptFileTypes)
        .flat()
        .some(ext => file.name.toLowerCase().endsWith(ext.replace('.', '')));
      const isValidSize = file.size <= 5 * 1000 * 1000;

      if (!isValidType || !isValidSize) {
        onUploadFailure({
          file,
          errors: [
            { code: isValidType ? 'file-too-large' : 'file-invalid-type', message: 'Invalid file' },
          ],
        } as FileRejection);
        return;
      }

      const base64Image = await toBase64(file);
      setReviewImageFile({ file, base64Image });
      setReviewImageFileType(file.type);
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptFileTypes,
    maxSize: 5 * 1000 * 1000, // 5 MB
    maxFiles: maxImages,
    multiple: false,
    onDrop,
    noClick: isMobile,
    validator: (file: File) => {
      const alreadyUploaded = uploadedImages.map(f => f.docName).includes(file.name);
      return alreadyUploaded ? { code: 'already-uploaded', message: 'Already uploaded' } : null;
    },
  });

  const uploadNextPageMessage = () => {
    let nextPageDescription: string;
    if (pageType === 'front-back') {
      nextPageDescription = totalSavedFiles ? 'Back' : 'Front';
    } else {
      nextPageDescription = `Page ${totalSavedFiles + 1}`;
    }
    return !IS_PERCH_ENERGY
      ? `Upload ${copy.documentType} (${nextPageDescription})`
      : `Upload ${copy.documentType}`;
  };

  const handleDropzoneClick = () => {
    if (isMobile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploaderProps = {
    getRootProps,
    getInputProps,
    isDragActive,
    handleDropzoneClick,
    fileInputRef,
    handleFileInputChange,
    uploadNextPageMessage,
  };

  useEffect(() => {
    if (uploadedImages && onRender) {
      onRender(uploadedImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container display="flex" flexDirection="column">
        {uploadedImages.map((uploadedImage, index) => {
          const isLastImage = index === uploadedImages.length - 1;
          const hasError = !!uploadedImage.error;

          const shouldRender = !hasError || (hasError && isLastImage);
          if (!shouldRender) return null;

          return (
            <ImagePreview
              errorCode={uploadedImage.error}
              key={uploadedImage.id}
              onDelete={() => onDelete(uploadedImage.id)}
              base64Image={uploadedImage.base64Image}
              uploaded={uploadedImage.error === undefined && isUploadComplete(uploadedImage)}
              isPdf={uploadedImage.type === 'application/pdf'}
              name={uploadedImage.name}
              disableDeletion={pending}
              validationStatus={validationStatuses?.[uploadedImage.id]}
            />
          );
        })}
      </Container>

      {!!acceptMoreFiles && !pending && (
        <DropzoneCt>
          <div className="dropzone-drag">
            {isMobile
              ? 'Upload from your device or take a photo'
              : 'Drag and drop the file or click on button'}
          </div>
          {isMobile ? (
            <MobileUploader {...uploaderProps} />
          ) : (
            <DesktopUploader {...uploaderProps} />
          )}

          <div className="dropzone-format">Max size: 5 MB | Formats: PNG, PDF, JPEG</div>
        </DropzoneCt>
      )}

      {!!reviewImageFile &&
        (reviewImageFileType === 'application/pdf' ? (
          <PdfReviewDialog
            base64Pdf={reviewImageFile.base64Image}
            onApprove={base64Pdf => {
              onUploadSuccess(reviewImageFile.file, base64Pdf);
              setReviewImageFile(null);
            }}
            onCancel={() => setReviewImageFile(null)}
          />
        ) : (
          <ImageReviewDialog
            base64Image={reviewImageFile.base64Image}
            fileType={reviewImageFile.file.type}
            onApprove={base64Image => {
              onUploadSuccess(reviewImageFile.file, base64Image);
              setReviewImageFile(null);
            }}
            onCancel={() => setReviewImageFile(null)}
          />
        ))}
    </>
  );
};
